import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiRequest } from '../services/apiClient';

/**
 * Lists documents for an employee profile.
 *
 *   status === 'forbidden' — server returned 403 (caller lacks
 *     employees.documents.read and isn't self-service). The
 *     consuming page renders a calm "Restricted" panel.
 *   status === 'idle'     — profileId is falsy.
 *   status === 'loading'  — initial fetch in flight.
 *   status === 'loaded'   — documents array populated.
 *   status === 'error'    — non-403 server / network error.
 */
export function useEmployeeDocuments(profileId, { includeArchived = false, documentType = null } = {}) {
  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState(profileId ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    if (!profileId) { setStatus('idle'); return; }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setError('');

    const params = new URLSearchParams();
    if (includeArchived) params.set('includeArchived', 'true');
    if (documentType) params.set('documentType', documentType);
    const qs = params.toString();
    const url = `/api/employees/profiles/${profileId}/documents${qs ? `?${qs}` : ''}`;

    try {
      const data = await apiRequest(url, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setStatus('loaded');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError && err.status === 403) {
        setDocuments([]);
        setStatus('forbidden');
        setError(err.message || 'Documents are restricted.');
        return;
      }
      setStatus('error');
      setError(err?.message || 'Unable to load employee documents.');
    }
  }, [profileId, includeArchived, documentType]);

  useEffect(() => {
    fetchDocs();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchDocs]);

  return { documents, status, isLoading: status === 'loading', error, refetch: fetchDocs };
}
